// Enter Page JavaScript

const ENTER_PAGE_TRANSITION_TYPE = 'enter-page-navigation';
const CUSTOM_TRANSITION_STORAGE = 'custom-transition-type';
const ENTER_PAGE_EXIT_FLAG = 'enter-page-exit-pending';
const ENTER_PAGE_HOME_DISMISSED = 'enter-page-home-dismissed';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @returns {boolean}
 */
function supportsCrossDocumentViewTransitions() {
  return (
    !prefersReducedMotion &&
    typeof CSS !== 'undefined' &&
    CSS.supports('view-transition-name', 'none') &&
    'PageSwapEvent' in window
  );
}

/**
 * @param {boolean} enable
 */
function toggleScrollLock(enable) {
  if (enable) {
    document.body.classList.add('enter-page-active');
  } else {
    document.body.classList.remove('enter-page-active');
  }
}

/**
 * Theme editor only: hide the overlay in place so the merchant is not trapped
 * behind it. Deliberately does NOT touch sessionStorage - re-selecting or
 * reloading the Enter Page section brings it straight back for editing.
 * @param {HTMLElement} enterPage
 */
function hideEnterPageForEditor(enterPage) {
  enterPage.classList.add('enter-page--editor-hidden');
  toggleScrollLock(false);
}

/**
 * Theme editor only: does this section/block event concern the Enter Page
 * section? Editor events fire for every section on the page, so without this
 * check selecting an unrelated section would pop the overlay back up.
 * @param {Event} event
 * @returns {boolean}
 */
function isEnterPageEditorEvent(event) {
  // Checked first: it is the one signal that does not depend on what element
  // the editor happened to dispatch the event on.
  const sectionId = /** @type {{ detail?: { sectionId?: string } }} */ (event).detail?.sectionId;
  if (sectionId) return sectionId === 'enter-page';

  const target = event.target;
  if (!(target instanceof Element)) return false;

  // The overlay itself, or an ancestor/descendant wrapper that holds it.
  if (target.closest('.enter-page') || target.closest('#enter-page')) return true;
  return Boolean(
    typeof target.querySelector === 'function' && target.querySelector('.enter-page, #enter-page')
  );
}

/**
 * Theme editor only: undo hideEnterPageForEditor.
 */
function showEnterPageForEditor() {
  document
    .querySelectorAll('.enter-page--editor-hidden')
    .forEach((el) => el.classList.remove('enter-page--editor-hidden'));
}

/**
 * @param {URL} url
 * @returns {string}
 */
function normalizePathname(url) {
  const path = url.pathname.replace(/\/+$/, '');
  return path || '/';
}

/**
 * @param {HTMLAnchorElement} link
 * @param {HTMLElement} enterPage
 * @returns {boolean}
 */
function isHomepageLink(url, enterPage) {
  const homeHref = enterPage.dataset.homeUrl || '/';

  try {
    const home = new URL(homeHref, window.location.href);
    return normalizePathname(home) === normalizePathname(url);
  } catch {
    return normalizePathname(url) === '/';
  }
}

/**
 * Reveal homepage content after the overlay is dismissed on the index.
 */
function revealHomepageInPlace() {
  const main = document.getElementById('MainContent');
  if (!main || prefersReducedMotion) return;

  main.classList.add('enter-page-content--revealing');
  main.addEventListener(
    'animationend',
    () => {
      main.classList.remove('enter-page-content--revealing');
    },
    { once: true }
  );
}

/**
 * Slide enter page away and show the homepage underneath (no navigation).
 *
 * @param {HTMLElement} enterPage
 */
function dismissEnterPageToHome(enterPage) {
  if (enterPage.dataset.enterPageDismissing === 'true') return;

  enterPage.dataset.enterPageDismissing = 'true';
  sessionStorage.setItem(ENTER_PAGE_HOME_DISMISSED, '1');
  toggleScrollLock(false);

  const homeHref = enterPage.dataset.homeUrl || '/';

  const completeDismiss = () => {
    document.documentElement.classList.add('enter-page-home-dismissed');
    document.getElementById('enter-page-exit-cover')?.remove();
    enterPage.remove();

    try {
      const homeUrl = new URL(homeHref, window.location.href);
      const currentUrl = new URL(window.location.href);

      if (
        normalizePathname(currentUrl) !== normalizePathname(homeUrl) ||
        currentUrl.search !== homeUrl.search ||
        currentUrl.hash
      ) {
        history.replaceState(history.state, '', `${homeUrl.pathname}${homeUrl.search}`);
      }
    } catch {
      // Keep current URL if parsing fails
    }
  };

  if (prefersReducedMotion) {
    completeDismiss();
    return;
  }

  // No exit cover on Home — homepage sits underneath and is revealed as the overlay slides up.

  let didComplete = false;
  const finishDismiss = () => {
    if (didComplete) return;
    didComplete = true;
    completeDismiss();
  };

  const startExitAnimation = () => {
    enterPage.classList.add('enter-page--exiting');

    enterPage.addEventListener(
      'animationend',
      (event) => {
        if (event.target !== enterPage || event.animationName !== 'slideOutTop') return;
        finishDismiss();
      },
      { once: true }
    );
  };

  requestAnimationFrame(() => {
    void enterPage.offsetWidth;
    startExitAnimation();
  });

  window.setTimeout(finishDismiss, getExitAnimationDurationMs() + 80);
}

/**
 * Skip overlay when user already chose Home this session.
 */
function initEnterPageHomeDismissState() {
  if (window.Shopify?.designMode) return;
  if (!sessionStorage.getItem(ENTER_PAGE_HOME_DISMISSED)) return;

  document.documentElement.classList.add('enter-page-home-dismissed');
  document.querySelector('.enter-page')?.remove();
  document.getElementById('enter-page-exit-cover')?.remove();
  toggleScrollLock(false);
}

/**
 * Subtle reveal when landing after leaving the enter page overlay.
 */
function shouldUseViewTransitionReveal() {
  return (
    'PageRevealEvent' in window &&
    !prefersReducedMotion &&
    sessionStorage.getItem(CUSTOM_TRANSITION_STORAGE) === ENTER_PAGE_TRANSITION_TYPE
  );
}

/**
 * CSS-only destination reveal when cross-document view transitions are unavailable.
 */
function initEnterPageRevealOnLoad() {
  if (prefersReducedMotion || !sessionStorage.getItem(ENTER_PAGE_EXIT_FLAG)) return;

  sessionStorage.removeItem(ENTER_PAGE_EXIT_FLAG);

  if (shouldUseViewTransitionReveal()) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  main.classList.add('enter-page-content--revealing');
  main.addEventListener(
    'animationend',
    () => {
      main.classList.remove('enter-page-content--revealing');
    },
    { once: true }
  );
}

/**
 * Apply view-transition-name on the enter overlay only (not homepage main — that caused a flash).
 *
 * @param {HTMLElement} enterPage
 */
function prepareEnterPageViewTransition(enterPage) {
  if (!supportsCrossDocumentViewTransitions()) return;

  enterPage.style.viewTransitionName = 'enter-page-overlay';
  enterPage.setAttribute('data-view-transition-type', ENTER_PAGE_TRANSITION_TYPE);
  enterPage.setAttribute('data-view-transition-triggered', 'true');
}

/**
 * Opaque layer under the sliding overlay so homepage content never shows through.
 *
 * @param {HTMLElement} enterPage
 */
function showExitCover() {
  if (document.getElementById('enter-page-exit-cover')) return;

  const cover = document.createElement('div');
  cover.id = 'enter-page-exit-cover';
  cover.className = 'enter-page-exit-cover';
  cover.setAttribute('aria-hidden', 'true');
  document.body.appendChild(cover);
}

/**
 * @returns {number}
 */
function getExitAnimationDurationMs() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--enter-page-exit-duration').trim();

  if (!raw) return 550;

  if (raw.endsWith('ms')) {
    return parseFloat(raw) || 550;
  }

  return (parseFloat(raw) || 0.55) * 1000;
}

/**
 * Hide preloader on exit and mark destination to skip it (one navigation only).
 */
function suppressPagePreloader() {
  sessionStorage.setItem(ENTER_PAGE_EXIT_FLAG, '1');

  const preloader = document.getElementById('page-preloader');
  if (!preloader) return;

  preloader.classList.add('hidden');
  preloader.remove();
}

/**
 * Hide storefront under the overlay, then navigate on the next frame so VT can capture state.
 *
 * @param {string} href
 */
function scheduleNavigate(href) {
  requestAnimationFrame(() => {
    void document.documentElement.offsetHeight;
    window.location.assign(href);
  });
}

/**
 * Start exit animation and navigate immediately (animation runs during page swap, not after).
 *
 * @param {string} href
 * @param {HTMLElement} enterPage
 */
function navigateWithEnterPageExit(href, enterPage) {
  if (enterPage.dataset.enterPageNavigating === 'true') {
    window.location.assign(href);
    return;
  }

  enterPage.dataset.enterPageNavigating = 'true';
  suppressPagePreloader();
  toggleScrollLock(false);

  if (prefersReducedMotion) {
    window.location.assign(href);
    return;
  }

  showExitCover();

  if (supportsCrossDocumentViewTransitions()) {
    prepareEnterPageViewTransition(enterPage);
    void enterPage.offsetWidth;
    scheduleNavigate(href);
    return;
  }

  document.body.classList.add('enter-page-navigating');

  let didNavigate = false;
  const completeNavigation = () => {
    if (didNavigate) return;
    didNavigate = true;
    window.location.assign(href);
  };

  const startExitAnimation = () => {
    enterPage.classList.add('enter-page--exiting');

    enterPage.addEventListener(
      'animationend',
      (event) => {
        if (event.target !== enterPage || event.animationName !== 'slideOutTop') return;
        completeNavigation();
      },
      { once: true }
    );
  };

  requestAnimationFrame(() => {
    void enterPage.offsetWidth;
    startExitAnimation();
  });

  window.setTimeout(completeNavigation, getExitAnimationDurationMs() + 80);
}

/**
 * @param {MouseEvent} event
 * @returns {boolean}
 */
function isModifiedNavigationClick(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0;
}

/**
 * @param {HTMLAnchorElement} link
 * @param {HTMLElement} enterPage
 * @returns {{ type: 'home' | 'navigate', url: URL } | null}
 */
function resolveEnterPageLink(link, enterPage) {
  if (!link.href || link.target === '_blank') return null;

  let url;

  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;

  if (isHomepageLink(url, enterPage)) {
    return { type: 'home', url };
  }

  const current = new URL(window.location.href);
  if (normalizePathname(current) === normalizePathname(url) && current.search === url.search) {
    return null;
  }

  return { type: 'navigate', url };
}

/**
 * Handles clicks on enter menu links.
 */
function initEnterPageNavigation() {
  const enterPage = document.querySelector('.enter-page');
  if (!enterPage) return;

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a.enter-menu__link[data-enter-page-nav], a[data-enter-page-nav].enter-menu__link');
      if (!(link instanceof HTMLAnchorElement) || !enterPage.contains(link)) return;

      const target = resolveEnterPageLink(link, enterPage);
      if (!target) return;

      if (isModifiedNavigationClick(event)) return;

      /* In the theme editor a link back to the homepage would reload the page
         and bring the overlay straight back, leaving the merchant stuck behind
         it. Dismiss it in place instead. Every other link is left alone so the
         editor navigates normally. */
      if (window.Shopify?.designMode) {
        if (target.type === 'home') {
          event.preventDefault();
          event.stopPropagation();
          hideEnterPageForEditor(enterPage);
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (target.type === 'home') {
        dismissEnterPageToHome(enterPage);
        return;
      }

      navigateWithEnterPageExit(target.url.href, enterPage);
    },
    true
  );
}

/**
 * Destination page hook — pairs with view-transitions.js (types/session) for enter menu navigation.
 */
function initEnterPagePagereveal() {
  if (!('PageRevealEvent' in window)) return;

  window.addEventListener('pagereveal', (event) => {
    if (!('viewTransition' in event) || !(event.viewTransition instanceof ViewTransition)) return;
    if (sessionStorage.getItem(CUSTOM_TRANSITION_STORAGE) !== ENTER_PAGE_TRANSITION_TYPE) return;

    document.getElementById('view-transition-render-blocker')?.remove();

    const main = document.getElementById('MainContent');
    main?.setAttribute('data-enter-page-destination-main', '');

    event.viewTransition.finished.finally(() => {
      main?.removeAttribute('data-enter-page-destination-main');
      sessionStorage.removeItem(CUSTOM_TRANSITION_STORAGE);
    });
  });
}

// Update datetime blocks every 60 seconds
function initEnterPageDateTime() {
  const datetimeElements = document.querySelectorAll('.enter-datetime__text[data-update="60"]');

  if (datetimeElements.length === 0) return;

  updateDateTime();

  window.setInterval(updateDateTime, 60000);

  function updateDateTime() {
    const now = new Date();
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    const formattedDate = now.toLocaleDateString('en-US', options);

    datetimeElements.forEach((element) => {
      element.textContent = formattedDate;
    });
  }
}

// Listen for theme editor changes - only unlock when section is disabled
function initEnterPageDesignMode() {
  if (!window.Shopify?.designMode) return;

  function checkEnterPageVisibility() {
    const enterPageContainer = document.getElementById('enter-page');
    const enterPage = document.querySelector('.enter-page');

    if (!enterPageContainer || !enterPage) {
      toggleScrollLock(false);
    }
  }

  document.addEventListener('shopify:section:load', () => {
    window.setTimeout(checkEnterPageVisibility, 100);
  });
  document.addEventListener('shopify:section:unload', checkEnterPageVisibility);


  const observer = new MutationObserver((mutations) => {
    let shouldCheck = false;

    mutations.forEach((mutation) => {
      if (mutation.type !== 'childList') return;

      mutation.addedNodes.forEach((node) => {
        if (
          node.nodeType === 1 &&
          (node.id === 'enter-page' || (node.querySelector && node.querySelector('#enter-page')))
        ) {
          shouldCheck = true;
        }
      });

      mutation.removedNodes.forEach((node) => {
        if (
          node.nodeType === 1 &&
          (node.id === 'enter-page' || (node.querySelector && node.querySelector('#enter-page')))
        ) {
          shouldCheck = true;
          toggleScrollLock(false);
        }
      });
    });

    if (shouldCheck) {
      window.setTimeout(checkEnterPageVisibility, 50);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  window.setTimeout(checkEnterPageVisibility, 200);
}

/**
 * Restores an editor-dismissed overlay. Registered unconditionally: the class
 * it clears is only ever set in the theme editor, and binding here means the
 * restore still works if Shopify sets designMode after this script evaluates.
 */
function initEnterPageEditorRestore() {
  document.addEventListener('shopify:section:load', (event) => {
    if (isEnterPageEditorEvent(event)) showEnterPageForEditor();
  });
  document.addEventListener('shopify:section:select', (event) => {
    if (isEnterPageEditorEvent(event)) showEnterPageForEditor();
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (isEnterPageEditorEvent(event)) showEnterPageForEditor();
  });
}

function bootEnterPage() {
  initEnterPageHomeDismissState();
  initEnterPageRevealOnLoad();
  initEnterPageNavigation();
  initEnterPagePagereveal();
  initEnterPageDateTime();
  initEnterPageEditorRestore();
  initEnterPageDesignMode();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootEnterPage);
} else {
  bootEnterPage();
}
